
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { recipeService } from '@/services/recipe';
import { RECIPES } from '@/constants/queryKeys';
import { ScalingFactor, CompositionInfo } from '@/types/shoppingList';
import { Recipe } from '@/types/recipe';
import RecipeIngredientsList from '@/components/recipe/RecipeIngredientsList';
import RecipeImage from '@/components/recipe/RecipeImage';
import { useUnitPreferences } from '@/hooks/useUnitPreferences';
import { Skeleton } from '@/components/ui/skeleton';
import { Users } from 'lucide-react';

interface RecipeIngredientsTabProps {
  recipes: string[];
  scalingFactors?: ScalingFactor[];
  compositions?: CompositionInfo[];
}

const RecipeIngredientsTab: React.FC<RecipeIngredientsTabProps> = ({
  recipes,
  scalingFactors = [],
  compositions = [],
}) => {
  const { preferredUnits } = useUnitPreferences();
  
  // Fetch all recipes to match against the shopping list recipes
  const { data: allRecipes = [], isLoading } = useQuery({
    queryKey: [RECIPES],
    queryFn: recipeService.getRecipes,
  });

  // Filter recipes to only include those in the shopping list
  const matchedRecipes = allRecipes.filter(recipe => 
    recipes.includes(recipe.title)
  );

  // Helper to format serving information (original only)
  const formatServingInfo = (recipe: Recipe) => {
    // Show recipe's own servings if available
    if (recipe.servings) {
      return ` (${recipe.servings} servings)`;
    }
    
    return '';
  };

  // Helper to get the correct structured ingredients based on unit preference
  const getStructuredIngredients = (recipe: Recipe) => {
    if (preferredUnits === 'metric') {
      return recipe.structured_ingredients_metric || recipe.structured_ingredients || null;
    } else {
      return recipe.structured_ingredients_imperial || recipe.structured_ingredients || null;
    }
  };

  // Group recipes by composition
  const groupedRecipes = React.useMemo(() => {
    const groups: {
      individual: Recipe[];
      compositions: Array<{
        composition: CompositionInfo;
        recipes: Recipe[];
      }>;
    } = {
      individual: [],
      compositions: []
    };

    // Get recipes that belong to compositions
    const compositionRecipeIds = new Set(
      compositions.flatMap(comp => comp.recipeIds)
    );

    // Separate individual recipes from composition recipes
    matchedRecipes.forEach(recipe => {
      const isInComposition = scalingFactors.some(sf => 
        sf.recipeTitle === recipe.title && sf.compositionName
      );

      if (!isInComposition) {
        groups.individual.push(recipe);
      }
    });

    // Group composition recipes
    compositions.forEach(composition => {
      const compositionRecipes = matchedRecipes.filter(recipe =>
        composition.recipeIds.includes(recipe.id)
      );

      if (compositionRecipes.length > 0) {
        groups.compositions.push({
          composition,
          recipes: compositionRecipes
        });
      }
    });

    return groups;
  }, [matchedRecipes, compositions, scalingFactors]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        {recipes.map((_, index) => (
          <div key={index} className="space-y-4">
            <Skeleton className="h-6 w-48" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-5/6" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (matchedRecipes.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No recipe details found for the recipes in this shopping list.</p>
        <p className="text-sm text-muted-foreground mt-2">
          The recipes might have been deleted or renamed.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-6 p-3 bg-muted/50 rounded-lg">
        <p className="text-sm text-muted-foreground font-medium">
          Showing original recipe ingredients (not scaled)
        </p>
      </div>
      
      {/* Individual Recipes */}
      {groupedRecipes.individual.length > 0 && (
        <div className="space-y-4">
          {groupedRecipes.individual.map((recipe) => {
            const structuredIngredients = getStructuredIngredients(recipe);
            
            return (
              <div key={recipe.id} className="bg-card border rounded-lg p-3 sm:p-4 shadow-sm">
                <div className="flex gap-2 sm:gap-4 mb-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 rounded-lg overflow-hidden">
                    <RecipeImage 
                      recipe={recipe} 
                      compact
                      className="rounded-lg"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-base sm:text-lg mb-2 sm:mb-3 text-foreground break-words leading-tight">
                      {recipe.title}{formatServingInfo(recipe)}
                    </h4>
                  </div>
                </div>
                
                <RecipeIngredientsList 
                  structuredIngredients={structuredIngredients}
                />
              </div>
            );
          })}
        </div>
      )}
      
      {/* Composition Groups */}
      {groupedRecipes.compositions.map((group) => (
        <div key={group.composition.id} className="space-y-4">
          <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-lg border border-primary/20">
            <Users size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-primary">
              {group.composition.name}
            </h3>
            <span className="text-sm text-muted-foreground">
              ({group.recipes.length} recipes)
            </span>
          </div>
          
          <div className="ml-4 space-y-4">
            {group.recipes.map((recipe) => {
              const structuredIngredients = getStructuredIngredients(recipe);
              
              return (
                <div key={recipe.id} className="bg-card border rounded-lg p-3 sm:p-4 shadow-sm">
                  <div className="flex gap-2 sm:gap-4 mb-4">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 rounded-lg overflow-hidden">
                      <RecipeImage 
                        recipe={recipe} 
                        compact
                        className="rounded-lg"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-base sm:text-lg mb-2 sm:mb-3 text-foreground break-words leading-tight">
                        {recipe.title}{formatServingInfo(recipe)}
                      </h4>
                    </div>
                  </div>
                  
                  <RecipeIngredientsList 
                    structuredIngredients={structuredIngredients}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}
      
      {/* Show any recipes from shopping list that weren't found */}
      {recipes.length > matchedRecipes.length && (
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg shadow-sm">
          <h4 className="font-medium text-amber-800 mb-2">Missing Recipe Details</h4>
          <ul className="text-sm text-amber-700 space-y-1">
            {recipes
              .filter(recipeName => !matchedRecipes.find(r => r.title === recipeName))
              .map((recipeName, index) => (
                <li key={index}>• {recipeName}</li>
              ))}
          </ul>
          <p className="text-xs text-amber-600 mt-2">
            These recipes were used in the shopping list but detailed information is not available.
          </p>
        </div>
      )}
    </div>
  );
};

export default RecipeIngredientsTab;
